import React, { useState } from 'react';
import { Button, Tag } from 'antd';
import {
  AimOutlined,
  BarChartOutlined,
  CalendarOutlined,
  DashboardOutlined,
  FileDoneOutlined,
  FlagOutlined,
  FundOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RiseOutlined,
  TableOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { ObjectCardEmpty } from '../../../components/layout/ObjectCard';
import {
  Callout,
  DashCard,
  Donut,
  KpiTile,
  LineChart,
  MetricRows,
  money,
  percent,
  shortMoney,
} from './DashboardCharts';
import {
  monthsPending,
  partialSum,
  profitSeries,
  progressPercent,
  ratioPercent,
  revenueSeries,
  yearEndOutlook,
  type DashboardMonth,
  type PartialSum,
} from './dashboardModel';
import { openPortfolio, quotesValue } from './ordersModel';
import { dk, type DashboardViewContext } from './viewContext';
import FigureBreakdown from './FigureBreakdown';
import { CostPartialMark, ReportLongTable } from '../ReportTab';
import MonthlySalesBars, { monthBarParts } from './MonthlySalesBars';
import ForecastPlanBoard from './ForecastPlanBoard';
import { breakdownOrders, figureMonths, type DashboardFigure } from './breakdownModel';

// "Pulpit zarządu" and "Analiza szczegółowa" views. Realization = the income
// statement's actual basis (recognized ledger); forecast = actual + the
// planned part moved by the scenario. Since PR2 (2026-10-29) the period comes
// from the page (one for every tab) and the views use the report primitives.

function signedMoney(value: number | null): string {
  if (value == null) return '—';
  return value > 0 ? `+ ${money(value)}` : value < 0 ? `− ${money(Math.abs(value))}` : money(0);
}

function incompleteNote(ctx: DashboardViewContext, incomplete: boolean): React.ReactNode {
  return incomplete ? <Callout title={dk(ctx.t, 'notes.incompleteProfitHint')}>{dk(ctx.t, 'notes.incompleteProfit')}</Callout> : null;
}

const hasTargets = (ctx: DashboardViewContext) => ctx.totals.revenueTarget != null || ctx.totals.profitTarget != null;

export function kpis(ctx: DashboardViewContext, selection?: { figure: DashboardFigure; select: (figure: DashboardFigure) => void }) {
  const { t, totals, language } = ctx;
  const pick = (figure: DashboardFigure) => (selection
    ? { onClick: () => selection.select(figure), selected: selection.figure === figure }
    : {});
  // The second line says what the number includes (PR2 review); the target
  // is added only when there is one — „Brak celu” lives in the indicators.
  const soldCount = breakdownOrders('sold', figureMonths('revenueActual', ctx.periodMonths), ctx.statement)?.length ?? null;
  const waitingCount = breakdownOrders('waitingInPeriod', ctx.periodMonths, ctx.statement)?.length ?? null;
  // With a target the line is the target part only (it must not be cut on a
  // 13″ MacBook); the description of the number stays in `title`.
  const withTarget = (base: string, value: number | null, target: number | null) => {
    const progress = progressPercent(value, target);
    if (progress == null) return { sub: base };
    const share = dk(t, 'kpi.targetShare', { percent: percent(progress, language) });
    return { sub: share, subTitle: dk(t, 'kpi.withTarget', { base, percent: percent(progress, language) }) };
  };
  const withDeviation = (base: string, value: number | null, target: number | null) => {
    if (target == null || value == null) return { sub: base };
    const amount = signedMoney(value - target);
    return { sub: dk(t, 'kpi.targetDeviation', { amount }), subTitle: dk(t, 'kpi.withDeviation', { base, amount }) };
  };
  const notes = {
    revenueActual: soldCount == null ? dk(t, 'kpi.revenueActualNote') : dk(t, 'kpi.revenueActualCount', { count: soldCount }),
    profitActual: dk(t, 'kpi.profitActualNote'),
    revenueForecast: waitingCount == null ? dk(t, 'kpi.revenueForecastNote') : dk(t, 'kpi.revenueForecastCount', { count: waitingCount }),
    profitForecast: dk(t, 'kpi.profitForecastNote'),
  };
  return {
    revenueActual: (
      <KpiTile
        testId="mgmt-kpi-revenue-actual"
        icon={<FileDoneOutlined />}
        iconTone="blue"
        {...pick('revenueActual')}
        label={dk(t, 'kpi.revenueActual')}
        value={money(totals.actualRevenue)}
        {...withTarget(notes.revenueActual, totals.actualRevenue, totals.revenueTarget)}
      />
    ),
    profitActual: (
      <KpiTile
        testId="mgmt-kpi-profit-actual"
        icon={<FundOutlined />}
        iconTone="green"
        {...pick('profitActual')}
        label={dk(t, 'kpi.profitActual')}
        value={money(totals.actualProfit.value)}
        marker={totals.actualProfit.incomplete && totals.actualProfit.value != null}
        markerHint={dk(t, 'notes.incompleteProfitHint')}
        tone={(totals.actualProfit.value ?? 0) < 0 ? 'negative' : undefined}
        {...withTarget(notes.profitActual, totals.actualProfit.value, totals.profitTarget)}
      />
    ),
    revenueForecast: (
      <KpiTile
        testId="mgmt-kpi-revenue-forecast"
        icon={<LineChartOutlined />}
        iconTone="indigo"
        {...pick('revenueForecast')}
        label={dk(t, 'kpi.revenueForecast')}
        value={money(totals.forecastRevenue)}
        {...withDeviation(notes.revenueForecast, totals.forecastRevenue, totals.revenueTarget)}
      />
    ),
    profitForecast: (
      <KpiTile
        testId="mgmt-kpi-profit-forecast"
        icon={<RiseOutlined />}
        iconTone="teal"
        {...pick('profitForecast')}
        label={dk(t, 'kpi.profitForecast')}
        value={money(totals.forecastProfit.value)}
        marker={totals.forecastProfit.incomplete && totals.forecastProfit.value != null}
        markerHint={dk(t, 'notes.incompleteProfitHint')}
        tone={(totals.forecastProfit.value ?? 0) < 0 ? 'negative' : undefined}
        {...withDeviation(notes.profitForecast, totals.forecastProfit.value, totals.profitTarget)}
      />
    ),
  };
}

/** Month indexes of the period on the whole-year charts; none for the whole year. */
function periodHighlight(ctx: DashboardViewContext): number[] | undefined {
  return ctx.wholeYear ? undefined : ctx.periodMonths.map((month) => month.index);
}

/** „cały rok · zaznaczony IV kw. 2026” under a whole-year chart when the period is shorter. */
function chartLead(ctx: DashboardViewContext): string | undefined {
  return ctx.wholeYear ? undefined : dk(ctx.t, 'charts.periodMarked', { period: ctx.periodLabel });
}

function revenueChart(ctx: DashboardViewContext, months: DashboardMonth[] = ctx.months) {
  const series = revenueSeries(months);
  return (
    <LineChart
      highlight={periodHighlight(ctx)}
      ariaLabel={dk(ctx.t, 'charts.revenueAria')}
      language={ctx.language}
      labels={ctx.monthLabels}
      series={[
        { key: 'target', name: dk(ctx.t, 'charts.target'), color: 'var(--mgmt-target)', values: series.target },
        { key: 'actual', name: dk(ctx.t, 'charts.actual'), color: 'var(--mgmt-series-1)', values: series.actual },
        { key: 'forecast', name: dk(ctx.t, `charts.forecast.${ctx.scenario}`), color: 'var(--mgmt-series-2)', values: series.forecast, dashed: true },
      ]}
    />
  );
}

function profitChart(ctx: DashboardViewContext) {
  const series = profitSeries(ctx.months);
  return (
    <LineChart
      highlight={periodHighlight(ctx)}
      ariaLabel={dk(ctx.t, 'charts.profitAria')}
      language={ctx.language}
      labels={ctx.monthLabels}
      series={[
        { key: 'target', name: dk(ctx.t, 'charts.profitTarget'), color: 'var(--mgmt-target)', values: series.target },
        { key: 'actual', name: dk(ctx.t, 'charts.actual'), color: 'var(--mgmt-series-1)', values: series.actual },
        { key: 'forecast', name: dk(ctx.t, `charts.forecast.${ctx.scenario}`), color: 'var(--mgmt-series-2)', values: series.forecast, dashed: true },
      ]}
    />
  );
}

// The costs of the „Zysk netto · prognoza” tile: the forecast basis of the
// same period (base scenario of the lines), so the donut sums to that tile's costs.
function costStructureItems(ctx: DashboardViewContext) {
  const { t, periodMonths } = ctx;
  const sum = (pick: (month: DashboardMonth) => number | null) => periodMonths.reduce((total, month) => total + (pick(month) ?? 0), 0);
  return [
    { key: 'goods', label: dk(t, 'costs.goods'), value: sum((month) => month.forecast.orderCosts) },
    { key: 'fixed', label: dk(t, 'costs.fixed'), value: sum((month) => month.forecast.operatingFixedCosts.total) },
    { key: 'depreciation', label: dk(t, 'costs.depreciation'), value: sum((month) => month.forecast.depreciation) },
    { key: 'financial', label: dk(t, 'costs.financial'), value: sum((month) => month.forecast.financialCosts) },
    { key: 'tax', label: dk(t, 'costs.tax'), value: sum((month) => month.forecast.incomeTax) },
  ];
}

function remainingPace(ctx: DashboardViewContext) {
  const { totals } = ctx;
  const months = Math.max(1, totals.remainingMonths);
  const remainingRevenue = totals.revenueTarget == null ? null : Math.max(0, totals.revenueTarget - totals.actualRevenue);
  const remainingProfit = totals.profitTarget == null || totals.actualProfit.value == null ? null : Math.max(0, totals.profitTarget - totals.actualProfit.value);
  return {
    revenue: remainingRevenue == null || totals.remainingMonths === 0 ? null : remainingRevenue / months,
    profit: remainingProfit == null || totals.remainingMonths === 0 ? null : remainingProfit / months,
  };
}

/** The one „Plan roku” button of a tab (owner 2026-10-29: two buttons on the Pulpit were one too many). */
function targetsButton(ctx: DashboardViewContext, label: 'set' | 'edit') {
  if (!ctx.canEditTargets) return null;
  return (
    <Button size="small" icon={<AimOutlined />} onClick={ctx.onEditTargets} data-testid="mgmt-edit-targets">
      {dk(ctx.t, `targets.${label}`)}
    </Button>
  );
}

// Month bars of the whole year; a click opens that month in „Sprzedaż i zamówienia”.
const MonthlySalesCard: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, language } = ctx;
  const withTargets = ctx.months.some((month) => month.revenueTarget != null);
  const describe = (month: DashboardMonth) => {
    const part = monthBarParts(month);
    return dk(t, 'monthBars.describe', {
      month: ctx.monthLabels[month.index] ?? month.month,
      invoiced: money(part.settled),
      planned: money(part.planned),
      target: part.target == null ? dk(t, 'monthBars.noTarget') : money(part.target),
    });
  };
  return (
    <DashCard
      title={dk(t, 'monthBars.title', { year: ctx.year })}
      icon={<BarChartOutlined />}
      tone="blue"
      lead={ctx.wholeYear
        ? dk(t, ctx.scenario === 'base' ? 'monthBars.hint' : 'monthBars.hintScenario')
        : dk(t, 'monthBars.hintPeriod', { period: ctx.periodLabel })}
      className="mgmt-month-card"
      extra={(
        <span className="mgmt-month-card__legend">
          <span><i className="is-settled" />{dk(t, 'monthBars.invoiced')}</span>
          <span><i className="is-forecast" />{dk(t, 'monthBars.planned')}</span>
          {withTargets && <span><i className="is-target" />{dk(t, 'monthBars.target')}</span>}
        </span>
      )}
    >
      <MonthlySalesBars
        months={ctx.months}
        labels={ctx.monthLabels}
        language={language}
        onOpen={ctx.openMonth!}
        describe={describe}
        highlight={ctx.wholeYear ? undefined : new Set(ctx.periodMonths.map((month) => month.index))}
      />
    </DashCard>
  );
};

/** An amount of a sum that may hold a ZK counted with its known cost only („*”). */
function partialMoney(ctx: DashboardViewContext, sum: PartialSum, testId: string) {
  return (
    <>
      {money(sum.value)}
      {sum.incomplete && sum.value != null && <CostPartialMark hint={dk(ctx.t, 'notes.incompleteProfitHint')} testId={testId} />}
    </>
  );
}

// „Przewidywane zamknięcie roku” (EAC, decision 2026-10-29-grupy-kosztow-i-budzet):
// the plan of the year next to the realization so far and the expected close
// (realization + forecast of the rest, scenario of the page).
const YearEndCard: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t } = ctx;
  const rows = yearEndOutlook(ctx.months);
  const anyPlan = rows.some((row) => row.plan != null);
  // A higher cost than planned is the worse direction.
  const worse = (key: string, deviation: number | null) => deviation != null && (key === 'fixedCosts' ? deviation > 0 : deviation < 0);
  return (
    <DashCard
      title={dk(t, 'yearEnd.title', { year: ctx.year })}
      icon={<FlagOutlined />}
      tone="indigo"
      lead={dk(t, ctx.scenario === 'base' ? 'yearEnd.lead' : 'yearEnd.leadScenario', { scenario: dk(t, `scenario.${ctx.scenario}`) })}
      testId="mgmt-year-end"
    >
      <div className="mgmt-table-wrap">
        <table className="mgmt-table mgmt-table--fixed mgmt-year-end-table">
          <colgroup>
            <col />
            <col className="mgmt-col-share" />
            <col className="mgmt-col-share" />
            <col className="mgmt-col-share" />
            <col className="mgmt-col-share" />
          </colgroup>
          <thead>
            <tr>
              <th>{dk(t, 'yearEnd.item')}</th>
              <th className="is-num">{dk(t, 'yearEnd.plan')}</th>
              <th className="is-num">{dk(t, 'yearEnd.actual')}</th>
              <th className="is-num" title={dk(t, 'yearEnd.closeHint')}>{dk(t, 'yearEnd.close')}</th>
              <th className="is-num">{dk(t, 'yearEnd.deviation')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} data-testid={`mgmt-year-end-${row.key}`}>
                <td>{dk(t, `yearEnd.rows.${row.key}`)}</td>
                <td className="is-num">{row.plan == null ? '—' : money(row.plan)}</td>
                <td className="is-num">{partialMoney(ctx, row.actual, 'mgmt-year-end-partial')}</td>
                <td className="is-num">{partialMoney(ctx, row.close, 'mgmt-year-end-partial')}</td>
                <td className={`is-num ${worse(row.key, row.deviation) ? 'is-negative' : ''}`}>{signedMoney(row.deviation)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!anyPlan && <Callout>{ctx.budgetFailed ? dk(t, 'targets.unavailable') : dk(t, 'yearEnd.noPlan', { year: ctx.year })}</Callout>}
    </DashCard>
  );
};

export const OverviewView: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, totals, language, statement, liquidity, orders, quotes } = ctx;
  const [figure, setFigure] = useState<DashboardFigure>('profitActual');
  const k = kpis(ctx, ctx.drillDown ? { figure, select: setFigure } : undefined);
  const pace = remainingPace(ctx);
  const portfolio = orders ? openPortfolio(orders) : null;
  const plannedRevenue = Math.max(0, totals.forecastRevenue - totals.actualRevenue);
  const costItems = costStructureItems(ctx);
  const costTotal = costItems.reduce((sum, item) => sum + Math.max(0, item.value), 0);
  const targets = hasTargets(ctx);
  // The plan profit computed from the margin and cost plans (backend), shown
  // when it differs from the entered net profit target.
  const calculatedProfit = partialSum(ctx.periodMonths.map((month) => month.plan?.netProfitCalculated ?? null)).value;
  const forecastProfitability = (
    <>
      {percent(ratioPercent(totals.forecastProfit.value, totals.forecastRevenue), language)}
      {totals.forecastProfit.incomplete && totals.forecastProfit.value != null
        && <CostPartialMark hint={dk(t, 'notes.incompleteProfitHint')} testId="mgmt-forecast-profitability-partial" />}
    </>
  );
  return (
    <>
      <div className="report-figures mgmt-figures">{k.revenueActual}{k.profitActual}{k.revenueForecast}{k.profitForecast}</div>
      {incompleteNote(ctx, totals.actualProfit.incomplete || totals.forecastProfit.incomplete)}
      {ctx.drillDown && <FigureBreakdown key={figure} ctx={ctx} figure={figure} />}
      {ctx.openMonth && <MonthlySalesCard ctx={ctx} />}
      {/* ADMIN only, like the forecast rows it lays out (2026-10-30). */}
      {ctx.openMonth && ctx.statement.forecastVsActual && <ForecastPlanBoard ctx={ctx} />}
      <div className="mgmt-grid mgmt-grid--twocol">
        <DashCard title={dk(t, 'overview.revenueChart')} icon={<LineChartOutlined />} tone="indigo" lead={chartLead(ctx)}>{revenueChart(ctx)}</DashCard>
        <DashCard
          title={dk(t, 'overview.indicators')}
          icon={<DashboardOutlined />}
          tone="teal"
          extra={targets ? targetsButton(ctx, 'edit') : undefined}
          testId="mgmt-indicators"
        >
          {targets ? (
            <>
              <MetricRows rows={[
                { label: dk(t, 'overview.targetProfitability'), value: percent(ratioPercent(totals.profitTarget, totals.revenueTarget), language) },
                ...(calculatedProfit != null && (totals.profitTarget == null || Math.abs(calculatedProfit - totals.profitTarget) >= 1)
                  ? [{ label: dk(t, 'overview.calculatedProfit'), value: money(calculatedProfit), title: dk(t, 'overview.calculatedProfitHint', { target: money(totals.profitTarget) }) }]
                  : []),
                { label: dk(t, 'overview.forecastProfitability'), value: forecastProfitability },
                { label: dk(t, 'overview.revenueGap'), value: totals.revenueTarget == null ? '—' : signedMoney(totals.forecastRevenue - totals.revenueTarget) },
                { label: dk(t, 'overview.profitGap'), value: totals.profitTarget == null || totals.forecastProfit.value == null ? '—' : signedMoney(totals.forecastProfit.value - totals.profitTarget) },
                { label: dk(t, 'overview.requiredRevenue'), value: money(pace.revenue), title: dk(t, 'overview.paceNote', { months: totals.remainingMonths }) },
                { label: dk(t, 'overview.requiredProfit'), value: money(pace.profit), title: dk(t, 'overview.paceNote', { months: totals.remainingMonths }) },
              ]}
              />
              <Callout title={dk(t, 'overview.paceNote', { months: totals.remainingMonths })}>
                {dk(t, 'overview.paceShort', { months: totals.remainingMonths })}
              </Callout>
            </>
          ) : (
            <>
              {/* Without targets: the indicators that need none, then one row that sets them. */}
              <MetricRows rows={[
                {
                  label: dk(t, 'overview.actualProfitability'),
                  value: (
                    <>
                      {percent(ratioPercent(totals.actualProfit.value, totals.actualRevenue), language)}
                      {totals.actualProfit.incomplete && totals.actualProfit.value != null
                        && <CostPartialMark hint={dk(t, 'notes.incompleteProfitHint')} testId="mgmt-actual-profitability-partial" />}
                    </>
                  ),
                },
                { label: dk(t, 'overview.forecastProfitability'), value: forecastProfitability },
                { label: dk(t, 'overview.plannedRevenue'), value: money(plannedRevenue), title: dk(t, 'overview.plannedRevenueHint') },
              ]}
              />
              <ObjectCardEmpty
                icon={<AimOutlined />}
                text={ctx.budgetFailed ? dk(t, 'targets.unavailable') : dk(t, 'targets.missingShort', { year: ctx.year })}
                action={ctx.budgetFailed ? undefined : targetsButton(ctx, 'set')}
              />
            </>
          )}
        </DashCard>
      </div>
      <YearEndCard ctx={ctx} />
      <div className="mgmt-grid mgmt-grid--third">
        <DashCard title={dk(t, 'overview.revenueStructure', { period: ctx.periodLabel })} icon={<PieChartOutlined />} tone="blue">
          <Donut
            language={language}
            center={shortMoney(totals.forecastRevenue + (quotes ? quotesValue(quotes) : 0), language)}
            centerCaption={dk(t, quotes && quotesValue(quotes) > 0 ? 'overview.revenueStructureCaption' : 'overview.revenueStructureCaptionNoQuotes')}
            emptyText={dk(t, 'empty')}
            items={[
              { key: 'actual', label: dk(t, 'overview.revenueInvoiced'), value: totals.actualRevenue },
              { key: 'planned', label: dk(t, 'overview.revenuePlanned'), value: plannedRevenue },
              { key: 'quotes', label: dk(t, 'overview.revenueQuotes'), value: quotes ? quotesValue(quotes) : 0 },
            ]}
          />
        </DashCard>
        <DashCard
          title={dk(t, 'overview.costStructureForecast', { period: ctx.periodLabel })}
          icon={<PieChartOutlined />}
          tone="orange"
          testId="mgmt-cost-structure"
        >
          <Donut
            language={language}
            center={shortMoney(costTotal, language)}
            centerCaption={dk(t, 'overview.costStructureCaption')}
            emptyText={dk(t, 'empty')}
            items={costItems}
          />
        </DashCard>
        <DashCard title={dk(t, 'overview.quick')} icon={<ThunderboltOutlined />} tone="green" lead={dk(t, 'overview.quickLead')}>
          <MetricRows rows={[
            { label: dk(t, 'overview.openOrders'), value: portfolio ? String(portfolio.count) : '—' },
            {
              label: dk(t, 'overview.toInvoice'),
              title: dk(t, 'overview.toInvoiceHint'),
              value: dk(t, 'countAmount', { count: statement.awaitingSettlementSummary.orderCount, amount: money(statement.awaitingSettlementSummary.revenue) }),
            },
            { label: dk(t, 'overview.overdueReceivables'), value: liquidity ? money(liquidity.receivables.overdue) : '—', tone: liquidity && liquidity.receivables.overdue > 0 ? 'negative' : undefined },
            { label: dk(t, 'overview.receivables30'), value: liquidity ? money(liquidity.receivables.dueIn30) : '—' },
            { label: dk(t, 'overview.payables30'), value: liquidity ? money(liquidity.payables.dueIn30) : '—' },
          ]}
          />
          <Callout title={dk(t, 'cash.basisShort')}>{dk(t, 'overview.quickNote')}</Callout>
        </DashCard>
      </div>
    </>
  );
};

function statusTag(ctx: DashboardViewContext, month: DashboardMonth) {
  if (month.isCurrent) return <Tag color="processing">{dk(ctx.t, 'status.current')}</Tag>;
  if (month.elapsed) return <Tag color="success">{dk(ctx.t, 'status.actual')}</Tag>;
  return <Tag color="warning">{dk(ctx.t, 'status.forecast')}</Tag>;
}

export const DetailsView: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, language, periodMonths, totals } = ctx;
  const closed = (month: DashboardMonth) => month.elapsed && !month.isCurrent;
  const valueOf = (month: DashboardMonth) => (closed(month) ? month.actualRevenue : month.forecastRevenue);
  const profitOf = (month: DashboardMonth) => (closed(month) ? month.actualProfit : month.forecastProfit);
  // „Razem” is the sum of the rows above it (closed months: realization; the
  // rest: forecast), so a plan still open in a closed month is not added twice.
  const rowsRevenue = periodMonths.reduce((sum, month) => sum + valueOf(month), 0);
  const pendingOf = (month: DashboardMonth) => monthsPending([month], closed(month) ? 'actual' : 'forecast');
  // „*” of the total after its months are summed (one rule for every range).
  const rowsPending = monthsPending(periodMonths.filter(closed), 'actual') || monthsPending(periodMonths.filter((month) => !closed(month)), 'forecast');
  const rowsProfit = partialSum(periodMonths.map(profitOf), [rowsPending]);
  const partialHint = dk(t, 'notes.incompleteProfitHint');
  const withTargets = hasTargets(ctx);
  return (
    <>
      {/* The figures of this tab are those of Rachunek wyników below (the Pulpit has the same four). */}
      <DashCard
        title={dk(t, 'details.table')}
        icon={<TableOutlined />}
        tone="blue"
        lead={dk(t, 'details.note')}
      >
        <ReportLongTable rowCount={periodMonths.length} testId="mgmt-details-table">
          <div className="mgmt-table-wrap">
            <table className="mgmt-table mgmt-table--compact">
              <thead>
                <tr>
                  <th>{dk(t, 'table.month')}</th>
                  {withTargets && <th className="is-num">{dk(t, 'table.revenueTarget')}</th>}
                  <th className="is-num">{dk(t, 'table.revenueActualForecast')}</th>
                  {withTargets && <th className="is-num" title={dk(t, 'table.deviation')}>{dk(t, 'planTable.diff')}</th>}
                  {withTargets && <th className="is-num">{dk(t, 'table.profitTarget')}</th>}
                  <th className="is-num">{dk(t, 'table.profitActualForecast')}</th>
                  <th className="is-num">{dk(t, 'table.profitability')}</th>
                  <th>{dk(t, 'table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {periodMonths.map((month) => {
                  const deviation = month.revenueTarget == null ? null : valueOf(month) - month.revenueTarget;
                  const pending = pendingOf(month) && profitOf(month) != null;
                  return (
                    <tr key={month.month}>
                      <td>{ctx.monthLabels[month.index]}</td>
                      {withTargets && <td className="is-num">{money(month.revenueTarget)}</td>}
                      <td className="is-num">{money(valueOf(month))}</td>
                      {withTargets && <td className={`is-num ${deviation != null && deviation < 0 ? 'is-negative' : ''}`}>{signedMoney(deviation)}</td>}
                      {withTargets && <td className="is-num">{money(month.profitTarget)}</td>}
                      <td className="is-num">{money(profitOf(month))}{pending && <CostPartialMark hint={partialHint} testId="mgmt-details-partial" />}</td>
                      <td className="is-num">{percent(ratioPercent(profitOf(month), valueOf(month)), language)}{pending && <CostPartialMark hint={partialHint} testId="mgmt-details-partial" />}</td>
                      <td>{statusTag(ctx, month)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>{dk(t, 'table.total', { period: ctx.periodLabel })}</td>
                  {withTargets && <td className="is-num">{money(totals.revenueTarget)}</td>}
                  <td className="is-num">{money(rowsRevenue)}</td>
                  {withTargets && <td className="is-num">{totals.revenueTarget == null ? '—' : signedMoney(rowsRevenue - totals.revenueTarget)}</td>}
                  {withTargets && <td className="is-num">{money(totals.profitTarget)}</td>}
                  <td className="is-num">{money(rowsProfit.value)}{rowsProfit.incomplete && rowsProfit.value != null && <CostPartialMark hint={partialHint} testId="mgmt-details-partial" />}</td>
                  <td className="is-num">{percent(ratioPercent(rowsProfit.value, rowsRevenue), language)}{rowsProfit.incomplete && rowsProfit.value != null && <CostPartialMark hint={partialHint} testId="mgmt-details-partial" />}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </ReportLongTable>
        {incompleteNote(ctx, rowsProfit.incomplete)}
      </DashCard>
      <PlanVsActualCard ctx={ctx} />
      <div className="mgmt-grid mgmt-grid--half">
        <DashCard title={dk(t, 'details.revenueChart', { year: ctx.year })} icon={<CalendarOutlined />} tone="indigo" lead={chartLead(ctx)}>{revenueChart(ctx)}</DashCard>
        <DashCard title={dk(t, 'details.profitChart', { year: ctx.year })} icon={<RiseOutlined />} tone="teal" lead={chartLead(ctx)}>{profitChart(ctx)}</DashCard>
      </div>
    </>
  );
};

// „Marża i koszty stałe: plan a wykonanie” of „Analiza szczegółowa”, one row
// per month of the period: closed months show the realization, the rest the
// forecast (as the table above); the plan comes from the budget.
const PlanVsActualCard: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, periodMonths } = ctx;
  const closed = (month: DashboardMonth) => month.elapsed && !month.isCurrent;
  const marginOf = (month: DashboardMonth) => (closed(month) ? month.actual.margin : month.forecastMargin);
  const fixedOf = (month: DashboardMonth) => (closed(month) ? month.actual : month.forecast).operatingFixedCosts.total;
  const ebitdaOf = (month: DashboardMonth) => {
    const margin = marginOf(month);
    return margin == null ? null : margin - fixedOf(month);
  };
  const pendingOf = (month: DashboardMonth) => monthsPending([month], closed(month) ? 'actual' : 'forecast');
  const withPlan = periodMonths.some((month) => month.plan && (month.plan.margin != null || month.plan.fixedCosts > 0));
  const diff = (value: number | null, plan: number | null | undefined) => (value == null || plan == null ? null : value - plan);
  const sum = (pick: (month: DashboardMonth) => number | null) => partialSum(periodMonths.map(pick)).value;
  const planSum = (pick: (month: DashboardMonth) => number | null) => {
    const values = periodMonths.map(pick).filter((value): value is number => value != null);
    return values.length ? values.reduce((total, value) => total + value, 0) : null;
  };
  const partialHint = dk(t, 'notes.incompleteProfitHint');
  // A loss reads as a loss: typographic minus and the danger colour (PR3 review).
  const amount = (value: number | null) => (value != null && value < 0 ? `− ${money(Math.abs(value))}` : money(value));
  const sign = (value: number | null) => (value != null && value < 0 ? 'is-negative' : '');
  const marked = (value: number | null, pending: boolean) => (
    <>{amount(value)}{pending && value != null && <CostPartialMark hint={partialHint} testId="mgmt-plan-partial" />}</>
  );
  const totals = {
    marginPlan: planSum((month) => month.plan?.margin ?? null),
    margin: sum(marginOf),
    fixedPlan: planSum((month) => (month.plan ? month.plan.fixedCosts : null)),
    fixed: sum(fixedOf),
    ebitdaPlan: planSum((month) => month.plan?.ebitda ?? null),
    ebitda: sum(ebitdaOf),
  };
  const anyPending = periodMonths.some(pendingOf);
  return (
    <DashCard
      title={dk(t, 'planTable.title')}
      icon={<AimOutlined />}
      tone="teal"
      lead={dk(t, 'planTable.lead')}
      extra={targetsButton(ctx, withPlan ? 'edit' : 'set')}
      testId="mgmt-plan-table"
    >
      {!withPlan ? (
        <ObjectCardEmpty
          icon={<AimOutlined />}
          text={ctx.budgetFailed ? dk(t, 'targets.unavailable') : dk(t, 'planTable.empty', { year: ctx.year })}
          action={ctx.budgetFailed ? undefined : targetsButton(ctx, 'set')}
        />
      ) : (
        <ReportLongTable rowCount={periodMonths.length} testId="mgmt-plan-table-rows">
          <div className="mgmt-table-wrap">
            <table className="mgmt-table mgmt-table--compact mgmt-plan-table">
              <thead>
                <tr>
                  <th>{dk(t, 'table.month')}</th>
                  <th className="is-num">{dk(t, 'planTable.marginPlan')}</th>
                  <th className="is-num" title={dk(t, 'planTable.actualForecastHint')}>{dk(t, 'planTable.margin')}</th>
                  <th className="is-num" title={dk(t, 'planTable.diffHint')}>{dk(t, 'planTable.diff')}</th>
                  <th className="is-num">{dk(t, 'planTable.fixedPlan')}</th>
                  <th className="is-num" title={dk(t, 'planTable.actualForecastHint')}>{dk(t, 'planTable.fixed')}</th>
                  <th className="is-num" title={dk(t, 'planTable.diffHint')}>{dk(t, 'planTable.diff')}</th>
                  <th className="is-num">{dk(t, 'planTable.ebitdaPlan')}</th>
                  <th className="is-num" title={dk(t, 'planTable.actualForecastHint')}>{dk(t, 'planTable.ebitda')}</th>
                </tr>
              </thead>
              <tbody>
                {periodMonths.map((month) => {
                  const pending = pendingOf(month);
                  const marginDiff = diff(marginOf(month), month.plan?.margin);
                  const fixedDiff = diff(fixedOf(month), month.plan ? month.plan.fixedCosts : null);
                  return (
                    <tr key={month.month} data-testid="mgmt-plan-row">
                      {/* Which basis the amounts of the row are (closed month: realization). */}
                      <td>
                        {ctx.monthLabels[month.index]}
                        <span className="report-muted"> · {dk(t, closed(month) ? 'planTable.basisActual' : 'planTable.basisForecast')}</span>
                      </td>
                      <td className={`is-num ${sign(month.plan?.margin ?? null)}`}>{amount(month.plan?.margin ?? null)}</td>
                      <td className={`is-num ${sign(marginOf(month))}`}>{marked(marginOf(month), pending)}</td>
                      <td className={`is-num ${marginDiff != null && marginDiff < 0 ? 'is-negative' : ''}`}>{signedMoney(marginDiff)}</td>
                      <td className="is-num">{money(month.plan ? month.plan.fixedCosts : null)}</td>
                      <td className="is-num">{money(fixedOf(month))}</td>
                      <td className={`is-num ${fixedDiff != null && fixedDiff > 0 ? 'is-negative' : ''}`}>{signedMoney(fixedDiff)}</td>
                      <td className={`is-num ${sign(month.plan?.ebitda ?? null)}`}>{amount(month.plan?.ebitda ?? null)}</td>
                      <td className={`is-num ${sign(ebitdaOf(month))}`}>{marked(ebitdaOf(month), pending)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>{dk(t, 'table.total', { period: ctx.periodLabel })}</td>
                  <td className={`is-num ${sign(totals.marginPlan)}`}>{amount(totals.marginPlan)}</td>
                  <td className={`is-num ${sign(totals.margin)}`}>{marked(totals.margin, anyPending)}</td>
                  <td className="is-num">{signedMoney(diff(totals.margin, totals.marginPlan))}</td>
                  <td className="is-num">{money(totals.fixedPlan)}</td>
                  <td className="is-num">{money(totals.fixed)}</td>
                  <td className="is-num">{signedMoney(diff(totals.fixed, totals.fixedPlan))}</td>
                  <td className={`is-num ${sign(totals.ebitdaPlan)}`}>{amount(totals.ebitdaPlan)}</td>
                  <td className={`is-num ${sign(totals.ebitda)}`}>{marked(totals.ebitda, anyPending)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </ReportLongTable>
      )}
    </DashCard>
  );
};
